import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, from } from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { FeedbackState } from './feedback.state';
import { selectFeedbackParams } from './feedback.selector';
import { ApiService } from 'src/app/services/api-services/api.service';
import {
  addFeedback,
  addFeedbackFailure,
  addFeedbackSuccess,
  getFeedbackById,
  getFeedbackByIdFailure,
  getFeedbackByIdSuccess,
  getFeedbackCategories,
  getFeedbackCategoriesFailure,
  getFeedbackCategoriesSuccess,
  getFeedbacks,
  getFeedbacksFailure,
  getFeedbacksSuccess,
  updateFeedback,
  updateFeedbackFailure,
  updateFeedbackSuccess,
} from './feedback.action';
import { FeedbackCategoryResponse } from './feedback.interface';
import { showError } from 'src/app/utility/common-logic';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class FeedbackEffects {
  constructor(
    private apiService: ApiService,
    private actions$: Actions,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {}

  private store = inject(Store<FeedbackState>);

  // Get feedback list
  getFeedbacks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getFeedbacks),
      withLatestFrom(this.store.select(selectFeedbackParams)),
      switchMap(([action, storeParams]) => {
        const params = action.params || storeParams || {};
        return from(this.apiService.getFeedback(params)).pipe(
          map((feedbacks: any) => getFeedbacksSuccess({ feedbacks })),
          catchError((error) => of(getFeedbacksFailure({ error }))),
        );
      }),
    ),
  );

  // Get feedback by ID
  getFeedbackById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getFeedbackById),
      mergeMap(({ id }) =>
        from(this.apiService.getFeedbackById(id)).pipe(
          map((feedback: any) => getFeedbackByIdSuccess({ feedback })),
          catchError((error) => of(getFeedbackByIdFailure({ error }))),
        ),
      ),
    ),
  );

  // Get all feedback categories
  getFeedbackCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getFeedbackCategories),
      mergeMap(() =>
        from(this.apiService.getFeedbackCategories()).pipe(
          map((categories: FeedbackCategoryResponse) =>
            getFeedbackCategoriesSuccess({ categories }),
          ),
          catchError((error) => of(getFeedbackCategoriesFailure({ error }))),
        ),
      ),
    ),
  );

  // Create feedback
  addFeedback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addFeedback),
      mergeMap(({ payload }) =>
        from(this.apiService.addFeedback(payload)).pipe(
          map((feedback: any) => addFeedbackSuccess({ feedback })),
          catchError((error) => {
            const err = error?.response?.data
              ? error?.response?.data[0]
              : this.translate.instant('msg.error._');
            showError(err, this.toastr, this.translate, true, true);
            return of(addFeedbackFailure({ error }));
          }),
        ),
      ),
    ),
  );

  // Update feedback
  updateFeedback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateFeedback),
      mergeMap(({ id, payload }) =>
        from(this.apiService.updateFeedback(id, payload)).pipe(
          map((feedback: any) => updateFeedbackSuccess({ feedback })),
          catchError((error) => {
            const err = error?.response?.data
              ? error?.response?.data[0]
              : this.translate.instant('msg.error._');
            showError(err, this.toastr, this.translate, true, true);
            return of(updateFeedbackFailure({ error }));
          }),
        ),
      ),
    ),
  );
}
